import React, { useEffect, useState } from "react";

// The framed certificate itself. Two assets exist per learner -- an A4
// landscape and an A4 portrait -- and the frame swaps aspect ratio to match
// whichever is showing.
//
// "responsive" is the public page's mode: landscape on desktop, portrait on
// mobile, decided by CSS and <picture> media queries rather than JavaScript,
// so only one asset downloads and there is no flash on first paint.

export const PORTRAIT_MEDIA = "(max-width: 760px)";
const LANDSCAPE_MEDIA = "(min-width: 761px)";

type Orientation = "landscape" | "portrait" | "responsive";

interface Props {
  orientation: Orientation;
  landscapeUrl?: string;
  portraitUrl?: string;
  // Deliberately name-free on the public page: the learner's name is printed
  // in the artwork itself, which is enough for a human to verify, and keeping
  // it out of the alt text is what stops the page ranking for their name.
  alt: string;
  className?: string;
  href?: string;
}

const CertificateStage: React.FC<Props> = ({
  orientation,
  landscapeUrl,
  portraitUrl,
  alt,
  className = "",
  href,
}) => {
  const fixedSrc = orientation === "portrait" ? portraitUrl : landscapeUrl;

  // Certificates are full-size A4 renders, so on a slow connection there is a
  // real wait. Showing the frame with nothing in it reads as broken, so the
  // frame says it is working. Only a genuine failure -- or an asset that does
  // not exist yet, since certificates are generated asynchronously after a
  // pass -- falls through to the apologetic message.
  const [status, setStatus] = useState<"loading" | "ready" | "failed">("loading");

  // Certificate templates are not a fixed shape -- the current landscape render
  // is nearer 1.9:1 than A4's 1.414:1, and each course has its own template. A
  // frame with a hard-coded ratio letterboxes the artwork inside it, so once
  // the image has loaded the frame takes the shape of what it is actually
  // holding. The CSS ratios remain the placeholder while it loads.
  const [ratio, setRatio] = useState<number | null>(null);

  useEffect(() => {
    setStatus("loading");
    setRatio(null);
  }, [landscapeUrl, portraitUrl, orientation]);

  const hasAsset =
    orientation === "responsive" ? !!(landscapeUrl || portraitUrl) : !!fixedSrc;

  const onLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0) setRatio(naturalWidth / naturalHeight);
    setStatus("ready");
  };

  const onError = () => setStatus("failed");

  const image =
    orientation === "responsive" ? (
      <picture>
        {landscapeUrl && <source media={LANDSCAPE_MEDIA} srcSet={landscapeUrl} />}
        {portraitUrl && <source media={PORTRAIT_MEDIA} srcSet={portraitUrl} />}
        <img
          className="cert-stage__img"
          src={landscapeUrl || portraitUrl}
          alt={alt}
          onLoad={onLoad}
          onError={onError}
        />
      </picture>
    ) : (
      <img
        className="cert-stage__img"
        src={fixedSrc}
        alt={alt}
        onLoad={onLoad}
        onError={onError}
      />
    );

  const overlay = () => {
    if (!hasAsset || status === "failed") {
      return (
        <div className="cert-stage__state" role="status">
          <span className="cert-stage__msg">This certificate is on its way</span>
          <span className="cert-stage__hint">
            It is still being prepared. Refresh in a moment.
          </span>
        </div>
      );
    }

    if (status === "loading") {
      return (
        <div className="cert-stage__state" role="status">
          <span className="cert-stage__spinner" aria-hidden="true" />
          <span className="cert-stage__msg">Loading your certificate</span>
        </div>
      );
    }

    return null;
  };

  const body = (
    <>
      {/* Kept mounted while loading so the browser actually fetches it; the
          overlay covers it until it has painted. */}
      {hasAsset && status !== "failed" && (
        <span
          className={`cert-stage__media ${
            status === "ready" ? "is-ready" : "is-loading"
          }`}
        >
          {image}
        </span>
      )}
      {overlay()}
    </>
  );

  return (
    <div
      className={`cert-stage cert-stage--${orientation} ${className}`.trim()}
      style={ratio ? { aspectRatio: `${ratio}` } : undefined}
    >
      {href && status === "ready" ? (
        <a
          className="cert-stage__slot"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {body}
        </a>
      ) : (
        <div className="cert-stage__slot">{body}</div>
      )}
    </div>
  );
};

export default CertificateStage;
